import { usePathname, useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { Pressable, Text, View } from 'react-native'
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { AlertBanner as SpecialAlertBanner } from '@/src/elements/alert-banner'
import { TEST_ID } from '@/src/general/constants/testId'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'

import { useAlertQuery } from '../../services/useAlertQuery'
import { useBannerAnimation } from './hooks/useBannerAnimation'

type TUseBannerStore = {
  alertsIdsConsulted: Record<number, boolean>
  addAlertIdConsulted: (alertId: number) => void
}

const useBannerStore = create(
  persist<TUseBannerStore>(
    (set) => ({
      alertsIdsConsulted: {},
      addAlertIdConsulted: (alertId: number) =>
        set((state) => ({ ...state, alertsIdsConsulted: { ...state.alertsIdsConsulted, [alertId]: true } })),
    }),
    {
      name: STORAGE_KEYS.ALERTS_CONSULTED,
      storage: createJSONStorage(() => storage),
    },
  ),
)

export const AlertBanner = (): ReactNode => {
  const { data } = useAlertQuery()
  const { navigate } = useRouter()
  const pathname = usePathname()
  const { top: topInset } = useSafeAreaInsets()

  const { alertsIdsConsulted, addAlertIdConsulted } = useBannerStore()
  const { animatedHeightY, animatedY, translateY, onLayout } = useBannerAnimation(data, alertsIdsConsulted)

  const animatedOpacity = useAnimatedStyle(() => ({
    opacity: interpolate(translateY.value, [0, 100], [1, 0], Extrapolation.CLAMP),
  }))

  if (data?.link && data?.link.includes(pathname)) return null

  return (
    <View className="relative">
      <Animated.View testID={TEST_ID.ALERT.PLACEHOLDER} style={[{ width: '100%' }, animatedHeightY]} />
      <Animated.View
        testID={TEST_ID.ALERT.BANNER}
        style={[{ maxWidth: '100%', position: 'absolute', width: '100%', top: topInset }, animatedY, animatedOpacity]}
        onLayout={onLayout}
        role="status"
      >
        <View className="min-w-full px-4 py-2">
          <Pressable
            pointerEvents="auto"
            onPress={() => {
              GTM.sendEvent({ event: EVENTS.ALERT_BANNER_CLICKED, bannerName: data?.title })
              if (data?.link) navigate(data?.link)
            }}
          >
            <SpecialAlertBanner
              className="my-2 max-h-[200px] w-full bg-secondary-bg-light"
              onClose={(e) => {
                // Stop event propagation to the parent touchable
                // otherwise it would trigger the redirection
                e?.stopPropagation()
                if (data) addAlertIdConsulted(data.id)
              }}
            >
              <Text className="w-[100vh] min-w-full text-center">{data?.text}</Text>
            </SpecialAlertBanner>
          </Pressable>
        </View>
      </Animated.View>
    </View>
  )
}
